import React from 'react';
import { Card, CardContent } from './ui/card';
import { 
  DollarSign, 
  Settings, 
  FlaskConical, 
  TrendingUp, 
  Shield, 
  Cloud, 
  FileCheck, 
  Zap,
  Users,
  Brain,
  Lock,
  BarChart3
} from 'lucide-react';

export const FeaturesSection = () => {
  const features = [
    {
      icon: DollarSign,
      title: 'Budget Control',
      description: 'Automated monitoring and reporting with real-time insights'
    },
    {
      icon: Settings,
      title: 'Custom Approvals',
      description: 'Workflows tailored to your business processes'
    },
    {
      icon: FlaskConical,
      title: 'Chemistry & Analytical Expertise',
      description: 'Specialized forms and modules for scientific workflows'
    },
    {
      icon: TrendingUp,
      title: 'Scalability',
      description: 'From 10 to 100+ users with ease and performance'
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Role-based access control with enterprise-grade security'
    },
    {
      icon: Cloud,
      title: 'Cloud Integration',
      description: 'Scale without infrastructure headaches'
    },
    {
      icon: FileCheck,
      title: 'Compliance',
      description: '21 CFR Part 11 ready with e-signatures and audit trails'
    },
    {
      icon: Zap,
      title: 'Reliability & Performance',
      description: 'Minimal downtime with high efficiency guarantees'
    },
    {
      icon: Users,
      title: 'User Experience',
      description: 'Easy-to-use, adoption-friendly interface design'
    }
  ];

  const advancedFeatures = [
    {
      icon: Brain,
      title: 'Price Master (AI-driven)',
      description: 'Find reliable, low-cost suppliers using machine learning algorithms',
      highlight: true
    },
    {
      icon: Lock,
      title: 'RBAC',
      description: 'Group- and department-level access controls for maximum security',
      highlight: false
    },
    {
      icon: BarChart3,
      title: 'Audit Trails',
      description: 'Ensure compliance and accountability across all workflows',
      highlight: false
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-4">
            Why Choose Genra
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Built for Industry Excellence
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive features designed to streamline operations, ensure compliance, and drive growth
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <Card key={index} className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 rounded-lg group-hover:bg-teal-600 transition-colors duration-300">
                      <feature.icon className="h-6 w-6 text-teal-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Advanced Features Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Advanced Features</h3>
            <p className="text-lg text-gray-600">Cutting-edge capabilities that set us apart</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {advancedFeatures.map((feature, index) => (
              <div 
                key={index} 
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 ${
                  feature.highlight 
                    ? 'border-teal-200 bg-teal-50 hover:border-teal-300' 
                    : 'border-gray-200 bg-white hover:border-teal-200'
                }`}
              >
                {feature.highlight && (
                  <div className="absolute -top-3 left-6">
                    <span className="bg-teal-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                      AI-Powered
                    </span>
                  </div>
                )}
                
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-lg ${
                    feature.highlight ? 'bg-teal-600' : 'bg-gray-100'
                  }`}>
                    <feature.icon className={`h-6 w-6 ${
                      feature.highlight ? 'text-white' : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Success Quote */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">Customer Success</h3>
            <p className="text-xl leading-relaxed max-w-4xl mx-auto">
              "Our ERP solutions empower organizations to integrate seamlessly, control budgets, 
              streamline workflows, and achieve compliance—helping teams focus on innovation rather than administration."
            </p>
            <div className="mt-6 text-teal-200">
              — Genra Tech Promise
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};